import { useId, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { canAccess, canWrite, fhir, type DirectoryPharmacy, type HeldRecord, type PharmacyLink, type PublicPractice } from '@curentia/shared';
import { ErrorText, Field, LockButton, Page, Submit } from '../components';
import { formatDate, formatDateTime } from '../lib/health';
import { FREQUENCIES, MEDICINES, TIMINGS, dosageText, suggestedDose, type FrequencyCode, type Timing } from '../lib/medicines';
import type { PracticeRecords } from '../lib/records';
import { useLoad } from '../lib/useLoad';
import { useAction, useDevice, useSnapshot } from '../state';
import { DispenseForm, DispensingBadge, DispensingHistory, dispenseEntries, type DispenseEntry } from './Dispensing';

const groups = (id: string) => (id.match(/.{1,4}/g) ?? []).join(' ');
const GENDERS: Record<string, string> = { female: 'Female', male: 'Male', other: 'Other', unknown: '' };

/** Whole years between a YYYY-MM-DD birth date and a time. */
export function ageOn(birthDate: string | undefined, at: string): number | null {
  if (!birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return null;
  const born = new Date(`${birthDate}T00:00:00`);
  const then = new Date(at);
  let years = then.getFullYear() - born.getFullYear();
  if (then.getMonth() < born.getMonth() || (then.getMonth() === born.getMonth() && then.getDate() < born.getDate())) years -= 1;
  return years >= 0 && years < 150 ? years : null;
}

function readable(record: HeldRecord): fhir.PrescriptionView | null {
  try {
    return fhir.readPrescription(record.resource as unknown as fhir.Bundle);
  } catch {
    return null;
  }
}

/**
 * A prescription as it is shown and printed: the practice's letterhead, the
 * doctor's name, qualifications and registration, the patient, the
 * medicines, advice and follow-up, and the digital signature with the
 * signing device's fingerprint. Everything comes from the signed document,
 * so the copy on the patient's phone and at the pharmacy prints the same.
 */
export function PrescriptionSheet({ record }: { record: HeldRecord }) {
  const view = readable(record);
  if (!view) return <p className="error">This prescription cannot be read by this version of Curentia.</p>;
  const signer = fhir.documentSigner(record.resource as unknown as fhir.Bundle);
  const age = ageOn(view.patient.birthDate, view.date);
  const sex = view.patient.gender ? GENDERS[view.patient.gender] : '';
  const practice = view.practice;
  const place = [practice?.address, practice?.city].filter(Boolean).join(', ');
  return (
    <article className="card print-sheet" aria-label="Prescription">
      <header className="letterhead">
        <h2>{practice?.name ?? 'Prescription'}</h2>
        {place ? <p>{place}</p> : null}
        {practice?.phone ? <p>Phone {practice.phone}</p> : null}
      </header>
      <section className="prescriber" aria-label="Doctor">
        <strong>{view.doctor.name}</strong>
        {view.doctor.qualifications ? <span>{view.doctor.qualifications}</span> : null}
        {view.doctor.registration ? (
          <span>
            Reg. no. {view.doctor.registration.number}, {view.doctor.registration.council}
          </span>
        ) : null}
      </section>
      <dl className="patient-line">
        <div>
          <dt>Patient</dt>
          <dd>{view.patient.name}</dd>
        </div>
        {age !== null || sex ? (
          <div>
            <dt>Age, sex</dt>
            <dd>{[age !== null ? `${age} years` : null, sex || null].filter(Boolean).join(', ')}</dd>
          </div>
        ) : null}
        <div>
          <dt>Date</dt>
          <dd>{formatDate(view.date)}</dd>
        </div>
      </dl>
      {view.diagnoses.length > 0 ? (
        <p>
          <strong>Diagnosis:</strong> {view.diagnoses.join('; ')}
        </p>
      ) : null}
      <p className="rx" aria-hidden="true">
        Rx
      </p>
      <ol className="medicines" aria-label="Medicines">
        {view.medicines.map((medicine) => (
          <li key={medicine.requestId}>
            <strong>{medicine.name.toUpperCase()}</strong>
            {medicine.dosage ? <span>{medicine.dosage}</span> : null}
            {medicine.instructions ? <span className="muted">{medicine.instructions}</span> : null}
          </li>
        ))}
      </ol>
      {view.advice ? (
        <div className="sheet-block">
          <strong>Advice</strong>
          {view.advice.split('\n').map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      ) : null}
      {view.followUp ? (
        <p>
          <strong>Follow-up:</strong> {[view.followUp.date ? formatDate(view.followUp.date) : null, view.followUp.note ?? null].filter(Boolean).join('. ')}
        </p>
      ) : null}
      {view.notes ? <p>{view.notes}</p> : null}
      {view.dispenser ? <p className="muted">Sent to {view.dispenser.name}</p> : null}
      {/* On paper the doctor can sign by hand as well, for a patient without the app. */}
      <p className="print-only hand-signature" aria-hidden="true">
        Signature
      </p>
      <footer className="signature">
        {signer ? (
          <>
            <span>
              Digitally signed by {view.doctor.name} on {formatDateTime(signer.when)}
            </span>
            <span>
              Signing key fingerprint <code data-testid="signing-fingerprint">{groups(signer.deviceId)}</code>
            </span>
          </>
        ) : (
          <span>Not digitally signed</span>
        )}
        <span className="muted">Prescription {view.id.replace(/^urn:uuid:/, '')}. Check it in the Curentia app.</span>
      </footer>
    </article>
  );
}

function PrintButton() {
  return (
    <button type="button" className="button secondary" onClick={() => window.print()}>
      Print or save as PDF
    </button>
  );
}

// ---- Writing a prescription -------------------------------------------------------------

interface MedicineRow {
  key: string;
  name: string;
  dose: string;
  frequency: FrequencyCode;
  timing: Timing;
  days: string;
  instructions: string;
}

const blankRow = (): MedicineRow => ({ key: globalThis.crypto.randomUUID(), name: '', dose: '', frequency: '1-0-1', timing: 'after food', days: '5', instructions: '' });

function MedicineFields({ row, index, listId, onChange, onRemove }: { row: MedicineRow; index: number; listId: string; onChange: (row: MedicineRow) => void; onRemove?: () => void }) {
  const frequencyId = useId();
  const timingId = useId();
  const set = (key: keyof MedicineRow) => (event: { target: { value: string } }) => onChange({ ...row, [key]: event.target.value });
  return (
    <fieldset className="medicine">
      <legend>Medicine {index + 1}</legend>
      <Field
        label="Medicine"
        list={listId}
        autoComplete="off"
        hint="Start typing to pick from the list, or type any medicine"
        value={row.name}
        onChange={(event) => {
          const name = event.target.value;
          // Picking a medicine from the list suggests a dose for its form, until the doctor types one.
          const dose = row.dose === '' || row.dose === suggestedDose(row.name) ? suggestedDose(name) : row.dose;
          onChange({ ...row, name, dose });
        }}
      />
      <div className="pair">
        <Field label="Dose" value={row.dose} onChange={set('dose')} placeholder="1 tablet" />
        <Field label="For how many days" inputMode="numeric" value={row.days} onChange={set('days')} />
      </div>
      <div className="field">
        <label htmlFor={frequencyId}>How often</label>
        <select id={frequencyId} value={row.frequency} onChange={set('frequency')}>
          {FREQUENCIES.map((f) => (
            <option key={f.code} value={f.code}>
              {f.label}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={timingId}>When</label>
        <select id={timingId} value={row.timing} onChange={set('timing')}>
          {TIMINGS.map((t) => (
            <option key={t} value={t}>
              {t === '' ? 'Any time' : t[0]?.toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
      </div>
      <Field label="Other instructions (optional)" value={row.instructions} onChange={set('instructions')} />
      {onRemove ? (
        <button type="button" className="button secondary small" onClick={onRemove}>
          Remove medicine {index + 1}
        </button>
      ) : null}
    </fieldset>
  );
}

/** The patient's details a prescription names. */
export interface PrescriptionPatient {
  id: string;
  details: fhir.PatientDetails;
}

/** Turns the form into the prescription's medicines, or a message. */
function medicinesFrom(rows: MedicineRow[]): fhir.MedicineInput[] | string {
  const named = rows.filter((row) => row.name.trim() !== '');
  if (named.length === 0) return 'Add at least one medicine.';
  const medicines: fhir.MedicineInput[] = [];
  for (const [i, row] of named.entries()) {
    const name = row.name.trim();
    if (name.length > 200) return `Use 200 characters or fewer for medicine ${i + 1}.`;
    if (row.dose.trim() === '') return `Enter the dose for ${name}.`;
    const days = row.days.trim() === '' ? null : Number(row.days);
    if (days !== null && (!Number.isInteger(days) || days < 1 || days > 365)) return `Enter between 1 and 365 days for ${name}, or leave it empty.`;
    const frequency = FREQUENCIES.find((f) => f.code === row.frequency);
    medicines.push({
      name,
      dosage: dosageText({ dose: row.dose, frequency: row.frequency, timing: row.timing, days }),
      ...(frequency?.timesPerDay ? { timesPerDay: frequency.timesPerDay } : {}),
      ...(days ? { durationDays: days } : {}),
      ...(row.instructions.trim() ? { instructions: row.instructions.trim().slice(0, 300) } : {}),
    });
  }
  return medicines;
}

const COUNTER = 'counter';
const NOBODY = '';

/**
 * The doctor's prescription form. Saving signs the prescription on this
 * device, keeps it at the practice's record holder, sends it to the
 * patient's phone when their account is linked, and to the pharmacy chosen:
 * the practice's own counter, which reads it at the record holder, or one of
 * the doctor's linked pharmacies. Otherwise the patient chooses a pharmacy.
 */
export function PrescriptionForm({ records, patient, onSaved }: { records: PracticeRecords; patient: PrescriptionPatient; onSaved: (record: HeldRecord, outcome: string) => void }) {
  const device = useDevice();
  const { account } = useSnapshot();
  const practice = records.practice;
  const links = useLoad(() => device.api.pharmacyLinks(), [device]);
  const linked = (links.data?.links ?? []).filter((l): l is PharmacyLink => l.status === 'active');
  const counter = practice.type === 'clinic' || practice.type === 'small_hospital';
  const [diagnosis, setDiagnosis] = useState('');
  const [rows, setRows] = useState<MedicineRow[]>(() => [blankRow()]);
  const [advice, setAdvice] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');
  const [sendTo, setSendTo] = useState(NOBODY);
  const { busy, error, setError, run } = useAction();
  const listId = useId();
  const diagnosisId = useId();
  const adviceId = useId();
  const today = new Date().toISOString().slice(0, 10);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!account?.registration) return setError('Your registration number is missing from your account.');
    const medicines = medicinesFrom(rows);
    if (typeof medicines === 'string') return setError(medicines);
    if (followUpDate && followUpDate < today) return setError('Choose a follow-up date from today on.');
    const pharmacy = linked.find((l) => l.pharmacy.id === sendTo)?.pharmacy;
    const details = patient.details;
    void run(async () => {
      const bundle = fhir.buildPrescriptionRecord({
        date: new Date().toISOString(),
        patient: {
          name: details.name,
          ...(details.gender ? { gender: details.gender } : {}),
          ...(details.birthDate ? { birthDate: details.birthDate } : {}),
          ...(details.mobile ? { mobile: details.mobile } : {}),
          ...(details.accountId ? { accountId: details.accountId } : {}),
          medicalRecord: details.medicalRecord ?? { value: patient.id, assigner: practice.name },
        },
        doctor: { name: account.name, registration: account.registration ?? { council: '', number: '' }, ...(account.qualifications ? { qualifications: account.qualifications } : {}) },
        practice: {
          name: practice.name,
          practiceId: practice.id,
          city: practice.city,
          ...(practice.address ? { address: practice.address } : {}),
          ...(practice.phone ? { phone: practice.phone } : {}),
        },
        diagnoses: diagnosis
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((text) => ({ text: text.slice(0, 200) })),
        medicines,
        ...(advice.trim() ? { advice: advice.trim().slice(0, 1000) } : {}),
        ...(followUpDate || followUpNote.trim() ? { followUp: { ...(followUpDate ? { date: followUpDate } : {}), ...(followUpNote.trim() ? { note: followUpNote.trim().slice(0, 200) } : {}) } } : {}),
        ...(pharmacy ? { dispenser: { name: pharmacy.name, practiceId: pharmacy.id } } : sendTo === COUNTER ? { dispenser: { name: `${practice.name} pharmacy counter`, practiceId: practice.id } } : {}),
      });
      const signed = await device.signDocument(bundle);
      const record = await records.save({
        id: globalThis.crypto.randomUUID(),
        patientId: patient.id,
        kind: 'document',
        hiType: 'Prescription',
        resource: signed as unknown as Record<string, unknown>,
        baseVersion: 0,
      });
      const sent: string[] = [];
      const failed: string[] = [];
      const from = { id: practice.id, name: practice.name };
      if (details.accountId) {
        try {
          if ((await device.sendToPatient(record, from, details.accountId)) > 0) sent.push(`${details.name}’s phone`);
        } catch {
          failed.push(`${details.name}’s phone`);
        }
      }
      if (pharmacy) {
        try {
          await device.sendToPharmacy(record, from, pharmacy.id);
          sent.push(pharmacy.name);
        } catch {
          failed.push(pharmacy.name);
        }
      }
      if (sendTo === COUNTER) sent.push('the pharmacy counter');
      const outcome = [
        'Prescription saved and signed.',
        sent.length > 0 ? `Sent to ${sent.join(' and ')}.` : '',
        failed.length > 0 ? `It could not be sent to ${failed.join(' or ')}. Print it for the patient instead.` : '',
      ]
        .filter(Boolean)
        .join(' ');
      onSaved(record, outcome);
    });
  };

  return (
    <form onSubmit={submit} noValidate className="prescription-form" aria-label="Write a prescription">
      <h3>Write a prescription</h3>
      <datalist id={listId}>
        {MEDICINES.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      <div className="field">
        <label htmlFor={diagnosisId}>Diagnosis</label>
        <textarea id={diagnosisId} rows={2} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} aria-describedby={`${diagnosisId}-hint`} />
        <span className="hint" id={`${diagnosisId}-hint`}>
          One per line
        </span>
      </div>
      {rows.map((row, index) => (
        <MedicineFields
          key={row.key}
          row={row}
          index={index}
          listId={listId}
          onChange={(next) => setRows(rows.map((r) => (r.key === row.key ? next : r)))}
          {...(rows.length > 1 ? { onRemove: () => setRows(rows.filter((r) => r.key !== row.key)) } : {})}
        />
      ))}
      <button type="button" className="button secondary small" onClick={() => setRows([...rows, blankRow()])}>
        Add another medicine
      </button>
      <div className="field spaced">
        <label htmlFor={adviceId}>Advice (optional)</label>
        <textarea id={adviceId} rows={3} value={advice} onChange={(e) => setAdvice(e.target.value)} />
      </div>
      <Field label="Follow-up date (optional)" type="date" min={today} value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} />
      <Field label="Follow-up note (optional)" value={followUpNote} onChange={(e) => setFollowUpNote(e.target.value)} />
      <fieldset>
        <legend>Pharmacy</legend>
        <div className="choices compact">
          <label className="check">
            <input type="radio" name="send-to" checked={sendTo === NOBODY} onChange={() => setSendTo(NOBODY)} />
            <span>The patient chooses</span>
          </label>
          {counter ? (
            <label className="check">
              <input type="radio" name="send-to" checked={sendTo === COUNTER} onChange={() => setSendTo(COUNTER)} />
              <span>This clinic’s pharmacy counter</span>
            </label>
          ) : null}
          {linked.map((link) => (
            <label key={link.id} className="check">
              <input type="radio" name="send-to" checked={sendTo === link.pharmacy.id} onChange={() => setSendTo(link.pharmacy.id)} />
              <span>
                {link.pharmacy.name}, {link.pharmacy.city}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <p className="muted">Saving signs the prescription with this device’s key.{patient.details.accountId ? ` It goes to ${patient.details.name}’s phone too.` : ' The patient has no linked Curentia account, so print it for them.'}</p>
      <ErrorText error={error} />
      <Submit busy={busy} busyText="Signing and sending…">
        Sign and save the prescription
      </Submit>
    </form>
  );
}

// ---- A practice's prescriptions ------------------------------------------------------------

function useRecords(practiceId: string) {
  const device = useDevice();
  const { practicesVersion } = useSnapshot();
  return useLoad(() => device.records(practiceId), [device, practiceId, practicesVersion]);
}

const titleOf = (view: fhir.PrescriptionView | null) => (view ? view.medicines.map((m) => m.name).join(', ') : 'Prescription');

/** Who a practice's copy of a prescription came from, for the pharmacy's list. */
function originOf(record: HeldRecord, practice: PublicPractice, view: fhir.PrescriptionView | null): string {
  const source = record.source;
  const doctor = view?.doctor.name ?? 'A doctor';
  if (!source) return `${doctor} · ${practice.name}`;
  return `${doctor} · ${source.practiceName ?? 'another practice'}${source.forwarded ? ' · sent by the patient' : ''}`;
}

/**
 * The prescriptions a practice holds, newest first: what a pharmacy received
 * from linked doctors and from patients, or what a clinic's doctors wrote,
 * which its pharmacy counter works from.
 */
export function PracticePrescriptionsScreen() {
  const { id = '' } = useParams();
  const { recordsVersion } = useSnapshot();
  const opened = useRecords(id);
  const records = opened.data;
  // Members who see dispensing also see where each prescription stands.
  const list = useLoad(async () => {
    if (!records) return null;
    const prescriptions = (await records.documents('Prescription')).reverse();
    const register = canAccess(records.roles, 'dispensing') ? await records.register() : null;
    return prescriptions.map((record) => ({ record, status: register ? statusAt(record, register.filter((d) => d.prescriptionId === record.id)) : null }));
  }, [records, recordsVersion]);
  const error = opened.error ?? list.error;
  return (
    <Page actions={<LockButton />}>
      <h1>Prescriptions</h1>
      {records ? <p className="muted">{records.practice.name}</p> : null}
      <ErrorText error={error} />
      <section className="card" aria-labelledby="prescriptions-heading">
        <h2 id="prescriptions-heading">{records?.practice.type === 'pharmacy' ? 'Received' : 'Written here'}</h2>
        {!list.data && !error ? <p className="muted">Loading…</p> : null}
        {list.data && list.data.length === 0 ? <p className="muted">No prescriptions yet.</p> : null}
        {list.data && list.data.length > 0 && records ? (
          <ul className="list" aria-label="Prescriptions">
            {list.data.map(({ record, status }) => {
              const view = readable(record);
              return (
                <li key={record.id}>
                  <Link className="list-link" to={`/practices/${id}/prescriptions/${record.id}`}>
                    <strong>
                      {view?.patient.name ?? 'Patient'} · {formatDate(record.date ?? record.createdAt)}
                    </strong>
                    <span>{titleOf(view)}</span>
                    <span className="muted">{originOf(record, records.practice, view)}</span>
                    {status ? <DispensingBadge status={status} /> : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
      <div className="links">
        <Link to={`/practices/${id}`}>Back</Link>
      </div>
    </Page>
  );
}

/** Where a held prescription stands against the practice's dispensing records for it. */
function statusAt(prescription: HeldRecord, dispenses: HeldRecord[]): fhir.DispensingStatus | null {
  const view = readable(prescription);
  return view ? fhir.dispensingProgress(view, dispenseEntries(dispenses.map((d) => ({ ...d, by: '' }))).map((e) => e.view)).status : null;
}

/**
 * One of a practice's prescriptions, to read and print. Pharmacists record
 * what they hand over against it, and see what was handed over before. The
 * prescribing doctor sees the notices pharmacies sent about it.
 */
export function PracticePrescriptionScreen() {
  const { id = '', recordId = '' } = useParams();
  const device = useDevice();
  const { account, recordsVersion } = useSnapshot();
  const opened = useRecords(id);
  const records = opened.data;
  const found = useLoad(async () => {
    if (!records) return null;
    const record = await records.get(recordId);
    const dispenses = canAccess(records.roles, 'dispensing')
      ? (await records.fetchRecords(record.patientId)).filter((r) => r.kind === 'dispense' && r.prescriptionId === record.id)
      : [];
    return { record, dispenses };
  }, [records, recordId, recordsVersion]);
  const [saved, setSaved] = useState(false);
  const record = found.data?.record;
  const view = record ? readable(record) : null;
  const notices = useLoad(
    async () => (account?.type === 'doctor' && view ? await device.doctorNotices().forPrescription(view.id) : []),
    [device, account?.type, view?.id, recordsVersion],
  );
  const here = dispenseEntries((found.data?.dispenses ?? []).map((d) => ({ ...d, by: records?.practice.name ?? 'This practice' })));
  const elsewhere = dispenseEntries((notices.data ?? []).map((n) => ({ id: n.key, resource: n.dispense.resource, by: n.pharmacy.name })));
  const entries = [...here, ...elsewhere].sort((a, b) => a.view.date.localeCompare(b.view.date));
  const progress = view ? fhir.dispensingProgress(view, here.map((e) => e.view)) : null;
  const dispenses = records ? canWrite(records.roles, 'dispensing') : false;
  // Doctors come here from the patient's timeline, pharmacists from the list of prescriptions.
  const back = record && records && canWrite(records.roles, 'prescriptions') ? `/practices/${id}/patients/${record.patientId}` : `/practices/${id}/prescriptions`;
  return (
    <Page actions={<LockButton />}>
      <h1>Prescription</h1>
      <ErrorText error={opened.error ?? found.error} />
      {record ? <PrescriptionSheet record={record} /> : !opened.error && !found.error ? <p className="muted">Loading…</p> : null}
      {record ? <PrintButton /> : null}
      {record?.source ? <p className="muted spaced">The doctor’s signature was checked when it arrived.</p> : null}
      {view && records && (dispenses || entries.length > 0) ? (
        <section className="card spaced" aria-labelledby="dispensing-heading">
          <h2 id="dispensing-heading">Dispensing</h2>
          {dispenses && progress ? <DispensingBadge status={progress.status} /> : null}
          {saved ? (
            <p className="note" role="status">
              Recorded. The patient and the prescribing doctor are told.
            </p>
          ) : null}
          {entries.length === 0 ? <p className="muted">Nothing handed over yet.</p> : <DispensingHistory prescription={view} entries={entries} />}
          {dispenses && record && progress && progress.status !== 'dispensed' ? (
            <DispenseForm
              key={here.length}
              records={records}
              prescription={record}
              view={view}
              progress={progress.medicines}
              onSaved={() => {
                setSaved(true);
                found.reload();
              }}
            />
          ) : null}
        </section>
      ) : null}
      <div className="links">
        <Link to={back}>Back</Link>
      </div>
    </Page>
  );
}

// ---- The patient's prescriptions -------------------------------------------------------------

/** The patient's prescriptions, newest first. */
export function MyPrescriptions() {
  const device = useDevice();
  const { recordsVersion } = useSnapshot();
  const { data, error } = useLoad(async () => (await device.personal().documents()).filter((r) => r.hiType === 'Prescription'), [device, recordsVersion]);
  return (
    <Page actions={<LockButton />}>
      <h1>Your prescriptions</h1>
      <p className="muted">Your doctors send them to this phone. Open one to send it to a pharmacy or print it.</p>
      <ErrorText error={error} />
      <section className="card" aria-labelledby="mine-heading">
        <h2 id="mine-heading">Prescriptions</h2>
        {!data && !error ? <p className="muted">Loading…</p> : null}
        {data && data.length === 0 ? <p className="muted">None yet.</p> : null}
        {data && data.length > 0 ? <PrescriptionList records={data} /> : null}
      </section>
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

export function PrescriptionList({ records }: { records: HeldRecord[] }) {
  return (
    <ul className="list" aria-label="Your prescriptions">
      {records.map((record) => {
        const view = readable(record);
        return (
          <li key={record.id}>
            <Link className="list-link" to={`/prescriptions/${record.id}`}>
              <strong>
                {view?.doctor.name ?? 'Your doctor'} · {formatDate(record.date ?? record.createdAt)}
              </strong>
              <span>{titleOf(view)}</span>
              <span className="muted">From {record.source?.practiceName ?? 'your doctor’s practice'}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** One of the patient's prescriptions: the sheet, what pharmacies handed over, printing, and sending it to a pharmacy. */
export function MyPrescription() {
  const { id = '' } = useParams();
  const device = useDevice();
  const { recordsVersion } = useSnapshot();
  const { data: record, error } = useLoad(() => device.personal().get(id), [device, id]);
  const dispenses = useLoad(
    async () => dispenseEntries((await device.personal().dispensesFor(id)).map((d) => ({ ...d, by: d.source?.practiceName ?? 'A pharmacy' }))),
    [device, id, recordsVersion],
  );
  const view = record ? readable(record) : null;
  return (
    <Page actions={<LockButton />}>
      <h1>Prescription</h1>
      <ErrorText error={error} />
      {record ? <PrescriptionSheet record={record} /> : record === null ? <p className="muted">This prescription is not on this phone.</p> : error ? null : <p className="muted">Loading…</p>}
      {record ? <PrintButton /> : null}
      {view && dispenses.data && dispenses.data.length > 0 ? <MyDispensing view={view} entries={dispenses.data} /> : null}
      {record ? <SendToPharmacy record={record} /> : null}
      <div className="links">
        <Link to="/prescriptions">Your prescriptions</Link>
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

/** What pharmacies handed over against the patient's prescription, with where it stands overall. */
function MyDispensing({ view, entries }: { view: fhir.PrescriptionView; entries: DispenseEntry[] }) {
  const { status, medicines } = fhir.dispensingProgress(view, entries.map((e) => e.view));
  const missing = medicines.filter((m) => m.handedOver.length === 0);
  return (
    <section className="card spaced" aria-labelledby="my-dispensing-heading">
      <h2 id="my-dispensing-heading">At the pharmacy</h2>
      <DispensingBadge status={status} />
      <DispensingHistory prescription={view} entries={entries} />
      {missing.length > 0 ? <p className="muted">Still to get: {missing.map((m) => m.medicine.name).join(', ')}.</p> : null}
    </section>
  );
}

function SendToPharmacy({ record }: { record: HeldRecord }) {
  const device = useDevice();
  const [city, setCity] = useState('');
  const [name, setName] = useState('');
  const [results, setResults] = useState<DirectoryPharmacy[] | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const recent = useLoad(() => device.recentPharmacies(), [device, sent]);
  const search = useAction();
  const send = useAction();
  const sendTo = (pharmacy: { id: string; name: string; city?: string }) =>
    void send.run(async () => {
      await device.forwardPrescription(record, pharmacy);
      setSent(pharmacy.name);
    });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (city.trim().length < 2) return search.setError('Enter the city.');
    void search.run(async () => {
      const { pharmacies } = await device.api.searchPharmacies(city.trim(), name.trim() || undefined);
      setResults(pharmacies);
    });
  };
  return (
    <section className="card" aria-labelledby="pharmacy-heading">
      <h2 id="pharmacy-heading">Send it to a pharmacy</h2>
      <p className="muted">Pick any verified pharmacy. It receives this prescription with your doctor’s signature and can check it.</p>
      {sent ? (
        <p className="note" role="status">
          Sent to {sent}.
        </p>
      ) : null}
      {recent.data && recent.data.length > 0 ? (
        <div className="stack spaced-below" role="group" aria-label="Pharmacies you used">
          {recent.data.map((pharmacy) => (
            <button key={pharmacy.id} type="button" className="button secondary" disabled={send.busy} onClick={() => sendTo(pharmacy)}>
              Send to {pharmacy.name}
              {pharmacy.city ? `, ${pharmacy.city}` : ''}
            </button>
          ))}
        </div>
      ) : null}
      <form onSubmit={submit} noValidate>
        <Field label="City" value={city} onChange={(e) => setCity(e.target.value)} />
        <Field label="Pharmacy name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
        <ErrorText error={search.error} />
        <Submit busy={search.busy} busyText="Searching…">
          Find a pharmacy
        </Submit>
      </form>
      {results && results.length === 0 ? <p className="muted">No verified pharmacy matches.</p> : null}
      {results && results.length > 0 ? (
        <ul className="list" aria-label="Pharmacies found">
          {results.map((pharmacy) => (
            <li key={pharmacy.id} className="member" aria-label={pharmacy.name}>
              <div>
                <strong>{pharmacy.name}</strong>
                <div className="muted">{pharmacy.city}</div>
              </div>
              <button type="button" className="button small" disabled={send.busy} onClick={() => sendTo(pharmacy)}>
                Send to {pharmacy.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <ErrorText error={send.error} />
    </section>
  );
}
